package com.swetdot.swetgram.ui.chat

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentSize
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.backhandler.BackHandler
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties
import com.swetdot.swetgram.domain.Message
import com.swetdot.swetgram.domain.MessageId
import com.swetdot.swetgram.domain.MessageState
import com.swetdot.swetgram.ui.design.Motion
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetPressFeedback
import com.swetdot.swetgram.ui.design.TouchTarget
import com.swetdot.swetgram.ui.design.reduceMotion
import com.swetdot.swetgram.ui.design.swetPressable
import com.swetdot.swetgram.ui.design.swetTween
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.chat_reaction_add
import swetgram.shared.generated.resources.chat_reaction_celebration
import swetgram.shared.generated.resources.chat_reaction_eyes
import swetgram.shared.generated.resources.chat_reaction_fire
import swetgram.shared.generated.resources.chat_reaction_heart
import swetgram.shared.generated.resources.chat_reaction_laugh
import swetgram.shared.generated.resources.chat_reaction_pray
import swetgram.shared.generated.resources.chat_reaction_remove
import swetgram.shared.generated.resources.chat_reaction_sad
import swetgram.shared.generated.resources.chat_reaction_surprised
import swetgram.shared.generated.resources.chat_reaction_thinking
import swetgram.shared.generated.resources.chat_reaction_thumbs_down
import swetgram.shared.generated.resources.chat_reaction_thumbs_up
import swetgram.shared.generated.resources.chat_reaction_angry
import swetgram.shared.generated.resources.chat_message_react
import kotlin.math.roundToInt

internal data class ReactionPickerTarget(
    val messageId: MessageId,
    val bounds: IntRect,
    val preferBelow: Boolean = false,
)

/** The row and the transparent timeline clearance use the same font-scaled measurement. */
@Composable
internal fun reactionPickerHeight(): Dp = with(LocalDensity.current) {
    32.sp.toDp().coerceAtLeast(TouchTarget.recommended) + Space.one
}

internal fun reactionPickerTarget(
    messageId: MessageId,
    bounds: IntRect,
    viewport: IntRect?,
    clearancePx: Int,
): ReactionPickerTarget = ReactionPickerTarget(
    messageId = messageId,
    bounds = bounds,
    preferBelow = viewport != null && bounds.top - viewport.top < clearancePx &&
        viewport.bottom - bounds.bottom >= clearancePx,
)

internal fun Rect.reactionBounds(): IntRect = IntRect(
    left.roundToInt(), top.roundToInt(), right.roundToInt(), bottom.roundToInt(),
)

internal fun canShowReactionPicker(message: Message?): Boolean =
    message != null && !message.isDeleted && message.isAcknowledged() &&
        message.state in setOf(MessageState.SENT, MessageState.READ)

/** Bounds are in window coordinates, not screen coordinates or LazyColumn item offsets. */
internal fun reactionPickerPosition(
    anchor: IntRect,
    viewport: IntRect,
    contentSize: IntSize,
    windowSize: IntSize,
    gapPx: Int,
    marginPx: Int,
    alignRight: Boolean,
    preferBelow: Boolean = false,
): IntOffset {
    val left = viewport.left.coerceIn(0, windowSize.width)
    val right = viewport.right.coerceIn(left, windowSize.width)
    val top = viewport.top.coerceIn(0, windowSize.height)
    val bottom = viewport.bottom.coerceIn(top, windowSize.height)
    val marginX = marginPx.coerceIn(0, ((right - left - contentSize.width) / 2).coerceAtLeast(0))
    val marginY = marginPx.coerceIn(0, ((bottom - top - contentSize.height) / 2).coerceAtLeast(0))
    val minX = left + marginX
    val maxX = (right - marginX - contentSize.width).coerceAtLeast(minX)
    val minY = top + marginY
    val maxY = (bottom - marginY - contentSize.height).coerceAtLeast(minY)
    val above = anchor.top - gapPx - contentSize.height
    val below = anchor.bottom + gapPx
    val y = when {
        preferBelow && below <= maxY -> below
        above >= minY -> above
        below <= maxY -> below
        anchor.top - top >= bottom - anchor.bottom -> above
        else -> below
    }.coerceIn(minY, maxY)
    val x = (if (alignRight) anchor.right - contentSize.width else anchor.left).coerceIn(minX, maxX)
    return IntOffset(x, y)
}

/** Emoji-only popup: no Surface, background, border, elevation, scrim or cancel button. */
@Composable
@OptIn(ExperimentalComposeUiApi::class)
internal fun FloatingReactionPicker(
    target: ReactionPickerTarget,
    message: Message,
    viewport: IntRect,
    mine: Boolean,
    onDismiss: () -> Unit,
    onReact: (String) -> Unit,
) {
    val density = LocalDensity.current
    val pickerHeight = reactionPickerHeight()
    val gapPx = with(density) { Space.one.roundToPx() }
    val maximumWidth = with(density) {
        (viewport.width - gapPx * 2).coerceAtLeast(1).toDp()
    }.coerceAtMost(TouchTarget.recommended * 6 + TouchTarget.recommended / 2)
    val positionProvider = remember(target, viewport, mine, gapPx) {
        object : PopupPositionProvider {
            override fun calculatePosition(
                anchorBounds: IntRect,
                windowSize: IntSize,
                layoutDirection: LayoutDirection,
                popupContentSize: IntSize,
            ): IntOffset = reactionPickerPosition(
                anchor = target.bounds,
                viewport = viewport,
                contentSize = popupContentSize,
                windowSize = windowSize,
                gapPx = gapPx,
                marginPx = gapPx,
                alignRight = mine != (layoutDirection == LayoutDirection.Rtl),
                preferBelow = target.preferBelow,
            )
        }
    }
    val reducedMotion = reduceMotion()
    var appeared by remember(target.messageId) { mutableStateOf(false) }
    LaunchedEffect(target.messageId) { appeared = true }
    val progress by animateFloatAsState(
        targetValue = if (appeared || reducedMotion) 1f else 0f,
        animationSpec = swetTween(Motion.fast),
        label = "floatingReactionsAppear",
    )

    // Keep the composer's focus/IME. Back is handled in the chat window instead of stealing focus.
    BackHandler(onBack = onDismiss)
    Popup(
        popupPositionProvider = positionProvider,
        onDismissRequest = onDismiss,
        properties = PopupProperties(
            focusable = false,
            dismissOnBackPress = true,
            dismissOnClickOutside = true,
        ),
    ) {
        Row(
            modifier = Modifier
                .testTag("chat_floating_reactions")
                .widthIn(max = maximumWidth)
                .height(pickerHeight)
                .graphicsLayer {
                    alpha = progress
                    val scale = if (reducedMotion) 1f else 0.92f + 0.08f * progress
                    scaleX = scale
                    scaleY = scale
                }
                .horizontalScroll(rememberScrollState())
                .padding(horizontal = Space.half, vertical = Space.half),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            (REACTION_OPTIONS + MORE_REACTION_OPTIONS).forEach { emoji ->
                val mineSelected = message.reactions.any { it.emoji == emoji && it.reactedByMe }
                val description = stringResource(reactionDescription(emoji))
                val clickLabel = stringResource(
                    if (mineSelected) Res.string.chat_reaction_remove else Res.string.chat_reaction_add,
                )
                Text(
                    text = emoji,
                    fontSize = 28.sp,
                    lineHeight = 32.sp,
                    maxLines = 1,
                    softWrap = false,
                    modifier = Modifier
                        .testTag("chat_reaction_$emoji")
                        .semantics {
                            contentDescription = description
                            selected = mineSelected
                        }
                        .swetPressable(
                            onClick = { onReact(emoji) },
                            onClickLabel = clickLabel,
                            feedback = SwetPressFeedback.Scale,
                        )
                        .sizeIn(minWidth = TouchTarget.recommended, minHeight = TouchTarget.recommended)
                        .wrapContentSize(Alignment.Center)
                        .graphicsLayer {
                            val scale = if (mineSelected && !reducedMotion) 1.08f else 1f
                            scaleX = scale
                            scaleY = scale
                        },
                )
            }
        }
    }
}

internal fun reactionDescription(emoji: String): StringResource = when (emoji) {
    "❤️" -> Res.string.chat_reaction_heart
    "👀" -> Res.string.chat_reaction_eyes
    "😢" -> Res.string.chat_reaction_sad
    "🎉" -> Res.string.chat_reaction_celebration
    "🔥" -> Res.string.chat_reaction_fire
    "👍" -> Res.string.chat_reaction_thumbs_up
    "😂" -> Res.string.chat_reaction_laugh
    "😮" -> Res.string.chat_reaction_surprised
    "😡" -> Res.string.chat_reaction_angry
    "🤔" -> Res.string.chat_reaction_thinking
    "🙏" -> Res.string.chat_reaction_pray
    "👎" -> Res.string.chat_reaction_thumbs_down
    else -> Res.string.chat_message_react
}
